import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sparkles, Loader2, ChevronDown, ChevronRight, Eye, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { generatePatternComboSuggestions, applyComboToSettings, applyComboToBot } from '@/services/pattern-combo-analytics-service';
import type { ComboSuggestion } from '@/services/pattern-combo-analytics-service';
import { ComboSimulationPreview } from './ComboSimulationPreview';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface PatternComboSuggestionsProps {
  focusedBotId?: string;
  focusedSettingId?: string;
  focusedBotName?: string;
}

export const PatternComboSuggestions: React.FC<PatternComboSuggestionsProps> = ({
  focusedBotId,
  focusedSettingId,
  focusedBotName
}) => {
  const [timeRange, setTimeRange] = useState('7d');
  const [comboSize, setComboSize] = useState<3 | 4 | 5>(4);
  const [patternMinLength, setPatternMinLength] = useState(1);
  const [patternMaxLength, setPatternMaxLength] = useState(5);
  const [minFrequency, setMinFrequency] = useState(2);
  const [minWinRateTarget, setMinWinRateTarget] = useState(60);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [suggestions, setSuggestions] = useState<ComboSuggestion[]>([]);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [previewCombo, setPreviewCombo] = useState<ComboSuggestion | null>(null);
  const [applyingCombo, setApplyingCombo] = useState<ComboSuggestion | null>(null);

  const isFocusedMode = !!focusedBotId;

  const handleGenerate = async () => {
    const targetBotId = focusedBotId;
    if (!targetBotId) {
      toast.error('Bot ID không hợp lệ');
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setProgressMessage('Đang khởi tạo...');
    setSuggestions([]);

    try {
      const results = await generatePatternComboSuggestions({
        botId: targetBotId,
        timeRange,
        comboSize,
        patternMinLength,
        patternMaxLength,
        minFrequency,
        minWinRateTarget,
      }, (stage, current, total) => {
        setProgress((current / total) * 100);
        setProgressMessage(stage);
      });

      setSuggestions(results);
      toast.success(`Đã tạo ${results.length} gợi ý combo pattern`);
    } catch (error: any) {
      console.error('Error generating suggestions:', error);
      if (error.message?.includes('Not enough')) {
        toast.error('Không đủ patterns phù hợp', {
          description: 'Thử giảm Min Frequency hoặc tăng Pattern Max Length'
        });
      } else {
        toast.error('Không thể tạo gợi ý pattern combo');
      }
    } finally {
      setIsGenerating(false);
      setProgress(0);
      setProgressMessage('');
    }
  };

  const handleApply = async (combo: ComboSuggestion) => {
    setApplyingCombo(combo);
  };

  const confirmApply = async () => {
    if (!applyingCombo || !focusedBotId) return;

    try {
      // If settingId exists, use it. Otherwise, auto-create setting for bot
      if (focusedSettingId) {
        await applyComboToSettings(focusedSettingId, applyingCombo);
      } else {
        await applyComboToBot(focusedBotId, applyingCombo);
      }
      
      toast.success('Đã áp dụng combo pattern thành công!');
      setApplyingCombo(null);
      setSuggestions([]); // Clear suggestions after applying
    } catch (error) {
      console.error('Error applying combo:', error);
      toast.error('Không thể áp dụng combo pattern');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            {isFocusedMode ? `Pattern Suggestions for ${focusedBotName}` : 'Generate Pattern Combo Suggestions'}
          </CardTitle>
          <CardDescription>
            {isFocusedMode 
              ? 'Brute-force optimizer: Test TẤT CẢ combinations để tìm combo pattern rules tốt nhất'
              : 'Brute-force optimizer: Test thousands of combinations để tìm optimal pattern rules'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Time Range */}
            <div className="space-y-2">
              <Label>Time Range</Label>
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">7 Days</SelectItem>
                  <SelectItem value="30d">30 Days</SelectItem>
                  <SelectItem value="90d">90 Days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Combo Size */}
            <div className="space-y-2">
              <Label>Combo Size</Label>
              <RadioGroup 
                value={comboSize.toString()} 
                onValueChange={(val) => setComboSize(parseInt(val) as 3 | 4 | 5)}
              >
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="3" id="size-3" />
                    <Label htmlFor="size-3">3</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="4" id="size-4" />
                    <Label htmlFor="size-4">4</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="5" id="size-5" />
                    <Label htmlFor="size-5">5</Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Pattern Min Length */}
            <div className="space-y-2">
              <Label htmlFor="min-length">Pattern Min Length</Label>
              <Input
                id="min-length"
                type="number"
                min={1}
                max={7}
                value={patternMinLength}
                onChange={(e) => setPatternMinLength(parseInt(e.target.value) || 1)}
              />
            </div>

            {/* Pattern Max Length */}
            <div className="space-y-2">
              <Label htmlFor="max-length">Pattern Max Length</Label>
              <Input
                id="max-length"
                type="number"
                min={1}
                max={7}
                value={patternMaxLength}
                onChange={(e) => setPatternMaxLength(parseInt(e.target.value) || 5)}
              />
            </div>

            {/* Min Frequency */}
            <div className="space-y-2">
              <Label htmlFor="min-freq">Min Frequency</Label>
              <Input
                id="min-freq"
                type="number"
                min={1}
                value={minFrequency}
                onChange={(e) => setMinFrequency(parseInt(e.target.value) || 2)}
              />
            </div>

            {/* Min Win Rate Target */}
            <div className="space-y-2">
              <Label htmlFor="minWinRate">Min Win Rate Target (%)</Label>
              <Input
                id="minWinRate"
                type="number"
                min={50}
                max={100}
                step={1}
                value={minWinRateTarget}
                onChange={(e) => setMinWinRateTarget(Number(e.target.value))}
              />
              <p className="text-xs text-muted-foreground">
                Chỉ hiển thị combos có win rate ≥ giá trị này (mặc định: 60%)
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          {isGenerating && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{progressMessage}</span>
                <span className="text-muted-foreground">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="w-full" />
            </div>
          )}

          {/* Generate Button */}
          <Button 
            onClick={handleGenerate} 
            disabled={isGenerating || !focusedBotId}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Đang tạo...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Suggestions
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results Table */}
      {suggestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top {suggestions.length} Suggestions</CardTitle>
            <CardDescription>
              Các combo pattern được xếp hạng theo win rate và số lượng trades
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[60px]">Rank</TableHead>
                    <TableHead>Win Rate</TableHead>
                    <TableHead>Trades</TableHead>
                    <TableHead>W/L</TableHead>
                    <TableHead>Rules</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suggestions.map((combo) => (
                    <React.Fragment key={combo.rank}>
                      <TableRow>
                        <TableCell className="font-medium">#{combo.rank}</TableCell>
                        <TableCell>
                          <Badge variant={combo.winRate >= 60 ? 'default' : 'secondary'}>
                            {combo.winRate.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell>{combo.totalTrades}</TableCell>
                        <TableCell>
                          <span className="text-green-600">{combo.wins}</span>
                          <span className="text-muted-foreground mx-1">/</span>
                          <span className="text-red-600">{combo.losses}</span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedRow(expandedRow === combo.rank ? null : combo.rank)}
                          >
                            {expandedRow === combo.rank ? (
                              <ChevronDown className="h-4 w-4 mr-1" />
                            ) : (
                              <ChevronRight className="h-4 w-4 mr-1" />
                            )}
                            Show {combo.rules.length}
                          </Button>
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setPreviewCombo(combo)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Preview
                          </Button>
                          <Button 
                            size="sm" 
                            variant="default"
                            onClick={() => handleApply(combo)}
                            disabled={!focusedSettingId}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {isFocusedMode ? 'Apply to This Bot' : 'Apply'}
                          </Button>
                        </TableCell>
                      </TableRow>
                      
                      {expandedRow === combo.rank && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-muted/50">
                            <div className="p-4">
                              <h4 className="font-semibold mb-3">Rules for Combo #{combo.rank}:</h4>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Lock</TableHead>
                                    <TableHead>Open</TableHead>
                                    <TableHead>Priority</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {combo.rules.map((rule, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell className="font-mono">{rule.lock}</TableCell>
                                      <TableCell className="font-mono">{rule.open}</TableCell>
                                      <TableCell>
                                        <Badge variant="outline">{rule.priority}</Badge>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Modal */}
      {previewCombo && (
        <ComboSimulationPreview
          open={!!previewCombo}
          onOpenChange={(open) => !open && setPreviewCombo(null)}
          combo={previewCombo}
        />
      )}

      {/* Apply Confirmation Dialog */}
      <AlertDialog open={!!applyingCombo} onOpenChange={() => setApplyingCombo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Áp dụng Combo Pattern?</AlertDialogTitle>
            <AlertDialogDescription>
              {isFocusedMode && (
                <p className="mb-2">
                  Áp dụng combo này cho <span className="font-semibold">{focusedBotName}</span>
                </p>
              )}
              Thao tác này sẽ xóa tất cả pattern rules hiện tại và thay thế bằng {applyingCombo?.rules.length} rules mới.
              <div className="mt-4 space-y-2">
                <p className="font-semibold">Win Rate dự kiến: {applyingCombo?.winRate.toFixed(1)}%</p>
                <p>Total Trades: {applyingCombo?.totalTrades} ({applyingCombo?.wins}W/{applyingCombo?.losses}L)</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction onClick={confirmApply}>
              Áp dụng
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
